import { ContractApiResponse, ParticipantResponse } from '../../types/api-responses';
import { ContractGuaranteeItem, ContractAsReceivableItem, ContractInstallmentItem } from '../../types/operations';
import { loadBusinessPartnerFromParticipants, sanitizeFileName } from '../helpers/operationHelpers';
import { BusinessPartnerData } from '../../components/form/BusinessPartnerManager';
import { CreditorPayload } from '../../../../../services/contractsManagementApi';
import { formatLocalDateToBR } from '../../../../../utils/DateUtils';

// ============================================
// METADATA HELPERS
// ============================================

export interface ContractMetadata {
    apiVersion?: string;
    amendmentReason?: string;
    amendmentNumber?: number;
    billingStatus: { billed?: boolean; billing_info?: string };
}

export const extractMetadata = (data: ContractApiResponse): ContractMetadata => ({
    apiVersion: data.apiName && data.apiVersion ? `${data.apiName} - ${data.apiVersion}` : undefined,
    amendmentReason: data.amendment_reason || undefined,
    amendmentNumber: data.amendment_number || undefined,
    billingStatus: { billed: data.billed, billing_info: data.billing_info || undefined },
});

// ============================================
// PARTICIPANTS HELPERS
// ============================================

export interface ParticipantInfo {
    uuid: string;
    name: string;
    document: string;
    id: number | null;
    person_id: string;
}

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

const mapParticipantToInfo = (participant: ParticipantResponse): ParticipantInfo => ({
    uuid: participant.person_id || EMPTY_UUID,
    name: participant.name || '',
    document: participant.document || '',
    id: participant.id ? Number(participant.id) : null,
    person_id: participant.person_id || EMPTY_UUID,
});

export const extractAssignor = (data: ContractApiResponse): ParticipantInfo | null => {
    const assignor = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    return assignor ? mapParticipantToInfo(assignor) : null;
};

export const extractRecipient = (data: ContractApiResponse): ParticipantInfo | null => {
    const recipient = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');
    return recipient ? mapParticipantToInfo(recipient) : null;
};

export const extractBusinessPartner = (data: ContractApiResponse): BusinessPartnerData | null =>
    loadBusinessPartnerFromParticipants(data.participants);

export const extractCreditor = (data: ContractApiResponse): CreditorPayload | null => {
    const creditor = data.creditor;
    if (!creditor || !creditor.personId) return null;
    return {
        name: creditor.name,
        govTaxId: creditor.govTaxId,
        personId: creditor.personId,
        type: creditor.type,
    };
};

// ============================================
// FILES HELPERS
// ============================================

export interface ExistingFiles {
    contractFile: string;
    artifactFiles: string[];
}

export const extractFilesFromSchema = (schema: any): ExistingFiles | null => {
    if (!schema.files) return null;

    // Nomes de arquivos que pertencem a contratos (cedido e garantia). Esses
    // arquivos sao registrados em files.artefacts apenas para o backend gerar
    // as URLs de upload, mas NAO devem aparecer na lista de artefatos, pois ja
    // sao exibidos/baixados no proprio contrato.
    const contractFileNames = collectContractFileNames(schema);

    const artifactFiles = (schema.files.artefacts || []).filter(
        (name: string) => !contractFileNames.has(sanitizeFileName(name || ''))
    );

    return {
        contractFile: schema.files.contract_file || '',
        artifactFiles,
    };
};

// Reune os nomes (sanitizados) dos arquivos de contratos cedidos e de garantia
// presentes no schema, para filtragem da lista de artefatos.
const collectContractFileNames = (schema: any): Set<string> => {
    const names = new Set<string>();

    const cededItems = schema.contracts_as_receivables?.items || schema.contracts?.items || [];
    const guaranteeItems = schema.collaterals?.assets?.contracts?.items || [];

    [...cededItems, ...guaranteeItems].forEach((item: any) => {
        if (item?.file_name) names.add(sanitizeFileName(item.file_name));
    });

    return names;
};

// ============================================
// CONTRACT GUARANTEE HELPERS
// ============================================

export interface ContractGuaranteeState {
    items: ContractGuaranteeItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

/**
 * Extracts contract guarantee data from schema supporting multiple formats:
 * 1. collaterals.assets.contracts.items (new format)
 * 2. schema.contracts.items (cessão root format)
 * 3. schema.contract_guarantee (legacy single object)
 */
/**
 * Extrai os contratos dados como garantia do schema para o estado do form.
 * Suporta o formato novo (aninhado, com installments[]) e o formato antigo
 * (plano, um contrato+parcela por item), agrupando os itens planos por
 * contrato (contract_number + documento do sacado).
 */
const genGuaranteeId = (): string =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const mapGuaranteeInstallmentFromSchema = (inst: any): ContractInstallmentItem => ({
    installment_id: inst.installment_id || genGuaranteeId(),
    contract_installment: inst.contract_installment ?? inst.title_installment ?? '',
    due_date: inst.due_date ? formatLocalDateToBR(inst.due_date) : '',
    installment_maturity_value: inst.installment_maturity_value ?? inst.maturity_value ?? 0,
});

// Extrai as parcelas de um item cru: usa installments[] (novo) ou os campos
// de parcela soltos no próprio item (legado plano).
const resolveGuaranteeInstallmentsFromRawItem = (item: any): ContractInstallmentItem[] => {
    if (Array.isArray(item.installments) && item.installments.length) {
        return item.installments.map(mapGuaranteeInstallmentFromSchema);
    }
    return [mapGuaranteeInstallmentFromSchema(item)];
};

const guaranteeGroupKey = (item: any): string =>
    `${(item.contract_number || '').trim().toUpperCase()}::${(item.drawee_document || '').trim().toUpperCase()}`;

// Valor do contrato = soma dos valores de vencimento das parcelas.
// Usado como fallback quando o schema traz contract_value ausente/zerado.
const sumInstallmentsMaturity = (installments: ContractInstallmentItem[]): number =>
    (installments || []).reduce((sum, inst) => sum + (inst.installment_maturity_value || 0), 0);

// Garante contract_value coerente: se vier 0/ausente do schema mas houver
// parcelas, calcula pela soma dos valores de vencimento das parcelas.
const resolveContractValue = (rawContractValue: number, installments: ContractInstallmentItem[]): number =>
    rawContractValue > 0 ? rawContractValue : sumInstallmentsMaturity(installments);

export const extractContractGuaranteeFromSchema = (schema: any): ContractGuaranteeState | null => {
    const items = resolveContractGuaranteeItems(schema);
    if (!items?.length) return null;

    const existingFiles = new Map<string, string>();
    const byKey = new Map<string, ContractGuaranteeItem>();

    items.forEach((item: any) => {
        const key = guaranteeGroupKey(item);
        const installments = resolveGuaranteeInstallmentsFromRawItem(item);
        const existing = byKey.get(key);

        if (existing) {
            // Contrato já visto (formato plano legado com parcelas repetindo cabeçalho):
            // apenas acumula as parcelas.
            existing.installments.push(...installments);
            return;
        }

        const id = item.collateral_id || genGuaranteeId();
        if (item.file_name) {
            existingFiles.set(id, item.file_name);
        }
        byKey.set(key, {
            collateral_id: id,
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            anticipated_value: item.anticipated_value || 0,
            file_name: item.file_name || '',
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || undefined,
            drawee_document_type: item.drawee_document_type || undefined,
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            installments,
        });
    });

    return {
        items: Array.from(byKey.values()).map((c) => ({
            ...c,
            contract_value: resolveContractValue(c.contract_value, c.installments),
        })),
        files: new Map(),
        existingFiles,
    };
};

const resolveContractGuaranteeItems = (schema: any): any[] | null => {
    if (schema.collaterals?.assets?.contracts?.items?.length) {
        return schema.collaterals.assets.contracts.items;
    }
    if (schema.contracts?.items?.length) {
        return schema.contracts.items;
    }
    if (schema.contract_guarantee) {
        return Array.isArray(schema.contract_guarantee)
            ? schema.contract_guarantee
            : [schema.contract_guarantee];
    }
    return null;
};

// ============================================
// CONTRACTS AS RECEIVABLES HELPERS
// ============================================

export interface ContractsAsReceivablesState {
    items: ContractAsReceivableItem[];
    files: Map<string, File>;
    existingFiles: Map<string, string>;
}

/**
 * Resolve os itens de contrato-como-recebível do schema.
 * Formato novo: operation_dictionary.contracts_as_receivables.
 * Compat: operation_dictionary.contracts (formato antigo da Cessão, quando
 * "Contrato cedido" era gravado como `contracts` no nível raiz).
 */
const resolveContractsAsReceivablesItems = (schema: any): any[] | null => {
    if (schema.contracts_as_receivables?.items?.length) {
        return schema.contracts_as_receivables.items;
    }
    if (schema.contracts?.items?.length) {
        return schema.contracts.items;
    }
    return null;
};

/**
 * Extrai os contratos dados como recebíveis do schema para o estado do form.
 * Suporta o formato novo (aninhado, com installments[]) e o formato antigo
 * (plano, um contrato+parcela por item), agrupando os itens planos por
 * contrato (contract_number + documento do sacado).
 */
const generateLocalId = (): string =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const mapInstallmentFromSchema = (inst: any): ContractInstallmentItem => ({
    installment_id: inst.installment_id || generateLocalId(),
    contract_installment: inst.contract_installment ?? inst.title_installment ?? '',
    operation_date: inst.operation_date ? formatLocalDateToBR(inst.operation_date) : '',
    present_value: inst.present_value || 0,
    due_date: inst.due_date ? formatLocalDateToBR(inst.due_date) : '',
    installment_maturity_value: inst.installment_maturity_value ?? inst.receivable_amount ?? inst.maturity_value ?? 0,
});

// Extrai as parcelas de um item cru: usa installments[] (novo) ou os campos
// de parcela soltos no próprio item (legado plano).
const resolveInstallmentsFromRawItem = (item: any): ContractInstallmentItem[] => {
    if (Array.isArray(item.installments) && item.installments.length) {
        return item.installments.map(mapInstallmentFromSchema);
    }
    // Legado plano: o próprio item carrega os campos de uma parcela.
    return [mapInstallmentFromSchema(item)];
};

const contractGroupKey = (item: any): string =>
    `${(item.contract_number || '').trim().toUpperCase()}::${(item.drawee_document || '').trim().toUpperCase()}`;

export const extractContractsAsReceivablesFromSchema = (schema: any): ContractsAsReceivablesState | null => {
    const items = resolveContractsAsReceivablesItems(schema);
    if (!items?.length) return null;

    const existingFiles = new Map<string, string>();
    const byKey = new Map<string, ContractAsReceivableItem>();

    items.forEach((item: any) => {
        const key = contractGroupKey(item);
        const installments = resolveInstallmentsFromRawItem(item);
        const existing = byKey.get(key);

        if (existing) {
            // Contrato já visto (formato plano legado com parcelas repetindo cabeçalho):
            // apenas acumula as parcelas.
            existing.installments.push(...installments);
            return;
        }

        const id = item.receivable_contract_id || item.collateral_id || generateLocalId();
        if (item.file_name) {
            existingFiles.set(id, item.file_name);
        }
        byKey.set(key, {
            receivable_contract_id: id,
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            contract_issue_date: item.contract_issue_date ? formatLocalDateToBR(item.contract_issue_date) : '',
            file_name: item.file_name || '',
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || undefined,
            drawee_document_type: item.drawee_document_type || undefined,
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_address_street: item.drawee_address_street || '',
            drawee_address_number: item.drawee_address_number || '',
            drawee_address_complement: item.drawee_address_complement || '',
            drawee_address_district: item.drawee_address_district || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            drawee_address_zip_code: item.drawee_address_zip_code || '',
            drawee_address_ibge_code: item.drawee_address_ibge_code || '',
            installments,
        });
    });

    return {
        items: Array.from(byKey.values()).map((c) => ({
            ...c,
            contract_value: resolveContractValue(c.contract_value, c.installments),
        })),
        files: new Map(),
        existingFiles,
    };
};
